'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { useMemo, useState } from 'react';
import { useWalletClient } from 'wagmi';
import type { VaultV2RecapSource } from '@/components/Bundler/ConfirmDialog/Recaps/hooks/useVaultV2RecapData';
import type { ForceDeallocationContext } from '@/components/common/VaultV2FeeRow';
import type { CheckoutEntryPoint } from '@/lib/analytics/client/events';
import type { TxSourceFlow } from '@/lib/shared/utils/flags.constants';
import VaultV2Recap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV2Recap';
import { ConfirmDialogV2 } from '@/components/ConfirmDialogV2';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useVaultWithdrawRequirements } from '@/hooks/operation/vault/useVaultWithdrawRequirements';
import { resolveWithdrawReviewBlock } from '@/hooks/operation/vault/withdrawReviewBlock';
import { buildReviewDialogParams, type CheckoutAttemptTrackingContext } from '@/lib/analytics/client/trackers/checkout-funnel';
import { getVaultV2WithdrawFlow } from '@/lib/observability/client/trackers/transaction';
import { getSdkTracking } from '@/lib/observability/client/trackers/transaction/sdk';
import { logger } from '@/lib/observability/logger';
import { useLogSdkBuildFailed } from '@/lib/observability/sdk';
import { InterfaceOperation } from '@/operations/operations.types';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';
import { useVaultV2SimulatedFinalPosition } from '../../../hooks/useVaultV2SimulatedFinalPosition';
import { useVaultV2WithdrawLogic } from '../../../hooks/useVaultV2WithdrawLogic';
import { getReviewErrorReason } from './reviewErrorReason';
import VaultV2OperationReviewDialog from './VaultV2OperationReviewDialog';
type WithdrawTransaction = NonNullable<ReturnType<typeof useVaultV2WithdrawLogic>['transaction']>;
const getWithdrawFlow = (transaction: WithdrawTransaction): Extract<TxSourceFlow, 'vaultV2Withdraw' | 'vaultV2ForceWithdraw'> => {
  try {
    return getVaultV2WithdrawFlow(transaction.buildTx().action.type);
  } catch {
    return 'vaultV2Withdraw';
  }
};
interface VaultV2WithdrawReviewDialogProps {
  vaultData: Parameters<typeof useVaultV2WithdrawLogic>[0]['vaultData'];
  vaultAddress: Address;
  inputAmount: bigint;
  isMax: boolean;
  onTxSuccess: () => void;
  entryPoint: CheckoutEntryPoint;
  /** The procedure the recap reads, matching the dialog the review was opened from. */
  recapSource?: VaultV2RecapSource;
}

/**
 * Re-runs the panel's withdraw logic, seeded with the amount and MAX choice the user reviewed.
 * `toggleDialog` keeps the element it was given, so a handle passed as a prop would never see a
 * later block; owning the logic here rebuilds the handle, and any force-deallocation plan, from
 * every polled vault snapshot.
 */
const VaultV2WithdrawReviewDialog = t0 => {
  const $ = _c(34);
  const {
    vaultData,
    vaultAddress,
    inputAmount,
    isMax,
    onTxSuccess,
    entryPoint,
    recapSource
  } = t0;
  let t1;
  if ($[0] !== inputAmount || $[1] !== isMax) {
    t1 = {
      amount: inputAmount,
      isMax
    };
    $[0] = inputAmount;
    $[1] = isMax;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1 || $[4] !== vaultAddress || $[5] !== vaultData) {
    t2 = {
      vaultData,
      vaultAddress,
      initialInput: t1
    };
    $[3] = t1;
    $[4] = vaultAddress;
    $[5] = vaultData;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const logic = useVaultV2WithdrawLogic(t2);
  const [hasHadTransaction, setHasHadTransaction] = useState(false);
  if (!hasHadTransaction && isDefined(logic.transaction)) {
    setHasHadTransaction(true);
  }
  const t3 = logic.inputMax?.value;
  const t4 = logic.error.vaultDataError ?? logic.error.userVaultPositionError;
  let t5;
  if ($[7] !== logic.inputAmount || $[8] !== logic.isLoading || $[9] !== logic.isMax || $[10] !== logic.localErrorMessage || $[11] !== logic.userVaultAssets || $[12] !== t3 || $[13] !== t4) {
    t5 = resolveWithdrawReviewBlock({
      isMax: logic.isMax,
      inputAmount: logic.inputAmount,
      maxInputAmount: t3,
      isLoading: logic.isLoading,
      errorMessage: logic.localErrorMessage,
      userVaultAssets: logic.userVaultAssets,
      readError: t4
    });
    $[7] = logic.inputAmount;
    $[8] = logic.isLoading;
    $[9] = logic.isMax;
    $[10] = logic.localErrorMessage;
    $[11] = logic.userVaultAssets;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    isAwaitingPosition,
    blockedReason
  } = t5;
  const t6 = vaultData?.asset.chain.id;
  const t7 = logic.userVaultAssets ?? ZERO;
  const t8 = (hasHadTransaction ? logic.isLoading : logic.isActionLoading) || isAwaitingPosition;
  let t9;
  if ($[15] !== blockedReason || $[16] !== entryPoint || $[17] !== logic.buildError || $[18] !== logic.finalPosition || $[19] !== logic.inputAmount || $[20] !== logic.isSnapshotPlaceholder || $[21] !== logic.retainedAssets || $[22] !== logic.sdkAction || $[23] !== logic.shareToken || $[24] !== logic.summaryFeeAssets || $[25] !== logic.summaryForceDeallocation || $[26] !== logic.transaction || $[27] !== onTxSuccess || $[28] !== recapSource || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== vaultAddress) {
    t9 = <VaultV2WithdrawReview vaultAddress={vaultAddress} chainId={t6} inputAmount={logic.inputAmount} userPosition={t7} finalPosition={logic.finalPosition} retainedAssets={logic.retainedAssets} feeAssets={logic.summaryFeeAssets} forceDeallocation={logic.summaryForceDeallocation} transaction={logic.transaction} buildError={logic.buildError} sdkAction={logic.sdkAction} shareToken={logic.shareToken} isSnapshotRefreshing={logic.isSnapshotPlaceholder} isLoading={t8} blockedReason={blockedReason} onTxSuccess={onTxSuccess} entryPoint={entryPoint} recapSource={recapSource} />;
    $[15] = blockedReason;
    $[16] = entryPoint;
    $[17] = logic.buildError;
    $[18] = logic.finalPosition;
    $[19] = logic.inputAmount;
    $[20] = logic.isSnapshotPlaceholder;
    $[21] = logic.retainedAssets;
    $[22] = logic.sdkAction;
    $[23] = logic.shareToken;
    $[24] = logic.summaryFeeAssets;
    $[25] = logic.summaryForceDeallocation;
    $[26] = logic.transaction;
    $[27] = onTxSuccess;
    $[28] = recapSource;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = vaultAddress;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
interface VaultV2WithdrawReviewProps {
  vaultAddress: Address;
  chainId: ChainId | undefined;
  inputAmount: bigint;
  userPosition: bigint;
  finalPosition: bigint | undefined;
  /** What a buffered MAX `forceRedeem` leaves deposited and never pays out; 0n elsewhere. */
  retainedAssets: bigint;
  feeAssets: bigint;
  forceDeallocation: ForceDeallocationContext | undefined;
  transaction: WithdrawTransaction | undefined;
  buildError: unknown;
  sdkAction: string;
  shareToken?: {
    decimals: number;
    symbol?: string;
  } | null;
  /** The vault or position snapshot is bridging a block poll; `transaction` is the previous block's. */
  isSnapshotRefreshing: boolean;
  isLoading: boolean;
  /** The refreshed panel logic rejects the reviewed request; shown as the error. */
  blockedReason: string | undefined;
  onTxSuccess: () => void;
  entryPoint: CheckoutEntryPoint;
  /** The procedure the recap reads, matching the dialog the review was opened from. */
  recapSource?: VaultV2RecapSource;
}
const VaultV2WithdrawReview = t0 => {
  const $ = _c(82);
  const {
    vaultAddress,
    chainId,
    inputAmount,
    userPosition,
    finalPosition,
    retainedAssets,
    feeAssets,
    forceDeallocation,
    transaction,
    buildError,
    sdkAction,
    shareToken,
    isSnapshotRefreshing,
    isLoading,
    blockedReason,
    onTxSuccess,
    entryPoint,
    recapSource
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    submitTransactions
  } = useTransactionContextV2();
  const {
    targetChain,
    account
  } = useWeb3Context();
  const {
    data: client,
    isLoading: isClientLoading
  } = useWalletClient();
  const [reviewSessionId] = useState(_temp);
  const isTransactionRefreshing = isSnapshotRefreshing;
  let t1;
  if ($[0] !== transaction || $[1] !== vaultAddress) {
    t1 = {
      label: "vaultV2Withdraw",
      vaultAddress,
      transaction
    };
    $[0] = transaction;
    $[1] = vaultAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    requirements,
    requirementsError,
    isLoading: isRequirementsLoading,
    isBridged: isRequirementsStale
  } = useVaultWithdrawRequirements(t1);
  const isPreparing = isLoading || isRequirementsLoading;
  let t2;
  if ($[3] !== account.isSafe || $[4] !== entryPoint || $[5] !== requirements || $[6] !== reviewSessionId) {
    t2 = buildReviewDialogParams({
      reviewSessionId,
      entryPoint,
      operationTypes: [InterfaceOperation.InterfaceOperationType.vaultV2Withdraw],
      isSafe: account.isSafe,
      requirements
    });
    $[3] = account.isSafe;
    $[4] = entryPoint;
    $[5] = requirements;
    $[6] = reviewSessionId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const reviewTrackingParams = t2;
  let t3;
  if ($[8] !== account.address || $[9] !== requirements || $[10] !== targetChain.id) {
    t3 = tryBuildSimulationAuthorizations({
      requirements,
      chainId: targetChain.id,
      from: account.address
    });
    $[8] = account.address;
    $[9] = requirements;
    $[10] = targetChain.id;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = t3;
  const t4 = buildError ?? requirementsError ?? simulationApprovalError;
  let t5;
  if ($[12] !== chainId || $[13] !== reviewSessionId || $[14] !== sdkAction || $[15] !== t4) {
    t5 = {
      sdkAction,
      chainId,
      reviewSessionId,
      error: t4
    };
    $[12] = chainId;
    $[13] = reviewSessionId;
    $[14] = sdkAction;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  useLogSdkBuildFailed(t5);
  let t6;
  if ($[17] !== chainId || $[18] !== finalPosition || $[19] !== isPreparing || $[20] !== simulationAuthorizations || $[21] !== transaction || $[22] !== vaultAddress) {
    t6 = {
      vaultAddress,
      chainId,
      transaction,
      simulationAuthorizations,
      fallbackPosition: finalPosition,
      isPreparing
    };
    $[17] = chainId;
    $[18] = finalPosition;
    $[19] = isPreparing;
    $[20] = simulationAuthorizations;
    $[21] = transaction;
    $[22] = vaultAddress;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const {
    displayedFinalPosition,
    isUnverifiedFinalPosition,
    status: simulationStatus,
    isRefreshing: isSimulationRefreshing
  } = useVaultV2SimulatedFinalPosition(t6);
  const isRefreshing = isTransactionRefreshing || isRequirementsStale || isSimulationRefreshing;
  let t7;
  if ($[24] !== account || $[25] !== blockedReason || $[26] !== client || $[27] !== displayedFinalPosition || $[28] !== feeAssets || $[29] !== forceDeallocation || $[30] !== inputAmount || $[31] !== isRequirementsLoading || $[32] !== isRequirementsStale || $[33] !== isUnverifiedFinalPosition || $[34] !== onTxSuccess || $[35] !== recapSource || $[36] !== requirements || $[37] !== requirementsError || $[38] !== retainedAssets || $[39] !== shareToken || $[40] !== simulationApprovalError || $[41] !== simulationStatus || $[42] !== submitTransactions || $[43] !== targetChain || $[44] !== toggleDialog || $[45] !== transaction || $[46] !== userPosition || $[47] !== vaultAddress) {
    t7 = checkoutTracking => {
      if (!transaction || isDefined(blockedReason) || !client || !account.isWalletTypeResolved || isRequirementsLoading || isRequirementsStale || requirements === undefined || requirementsError !== null && requirementsError !== undefined || simulationStatus === "blacklist" || simulationApprovalError !== undefined) {
        return;
      }
      submitTransactions(client, {
        actionType: InterfaceOperation.InterfaceOperationType.vaultV2Withdraw,
        flow: getWithdrawFlow(transaction),
        transaction,
        ...getSdkTracking(transaction),
        requirements,
        onTxSuccess,
        chain: targetChain,
        account,
        checkoutTracking
      }).catch(error => {
        logger.error(error instanceof Error ? error : new Error(String(error)), {
          tags: {
            component: "VaultV2WithdrawReviewDialog"
          }
        });
        toggleDialog(false);
      });
      toggleDialog(<ConfirmDialogV2 txMessage="Vault V2 Withdraw" token={shareToken} operationContent={<VaultV2Recap type="vaultV2Withdraw" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} feeAssets={feeAssets} retainedAssets={retainedAssets} forceDeallocation={forceDeallocation} isSimulationFallback={isUnverifiedFinalPosition} source={recapSource} />} />, {
        closable: false
      });
    };
    $[24] = account;
    $[25] = blockedReason;
    $[26] = client;
    $[27] = displayedFinalPosition;
    $[28] = feeAssets;
    $[29] = forceDeallocation;
    $[30] = inputAmount;
    $[31] = isRequirementsLoading;
    $[32] = isRequirementsStale;
    $[33] = isUnverifiedFinalPosition;
    $[34] = onTxSuccess;
    $[35] = recapSource;
    $[36] = requirements;
    $[37] = requirementsError;
    $[38] = retainedAssets;
    $[39] = shareToken;
    $[40] = simulationApprovalError;
    $[41] = simulationStatus;
    $[42] = submitTransactions;
    $[43] = targetChain;
    $[44] = toggleDialog;
    $[45] = transaction;
    $[46] = userPosition;
    $[47] = vaultAddress;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  const handleContinue = t7;
  let t8;
  if ($[49] !== account.isWalletTypeResolved || $[50] !== account.isWrongChain || $[51] !== blockedReason || $[52] !== client || $[53] !== isRequirementsLoading || $[54] !== isTransactionRefreshing || $[55] !== requirements || $[56] !== requirementsError || $[57] !== simulationApprovalError || $[58] !== transaction) {
    t8 = !transaction && !isTransactionRefreshing || isDefined(blockedReason) || !client || !account.isWalletTypeResolved || account.isWrongChain || requirements === undefined || requirementsError !== null && requirementsError !== undefined || isRequirementsLoading || simulationApprovalError !== undefined;
    $[49] = account.isWalletTypeResolved;
    $[50] = account.isWrongChain;
    $[51] = blockedReason;
    $[52] = client;
    $[53] = isRequirementsLoading;
    $[54] = isTransactionRefreshing;
    $[55] = requirements;
    $[56] = requirementsError;
    $[57] = simulationApprovalError;
    $[58] = transaction;
    $[59] = t8;
  } else {
    t8 = $[59];
  }
  const t9 = !transaction && !isLoading && !isTransactionRefreshing;
  const t10 = !client && !isClientLoading;
  const t11 = requirementsError ?? simulationApprovalError;
  let t12;
  if ($[60] !== blockedReason || $[61] !== t10 || $[62] !== t11 || $[63] !== t9) {
    t12 = getReviewErrorReason({
      blockedReason,
      isTransactionMissing: t9,
      isWalletClientMissing: t10,
      requirementsError: t11
    });
    $[60] = blockedReason;
    $[61] = t10;
    $[62] = t11;
    $[63] = t9;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  let t13;
  if ($[65] !== displayedFinalPosition || $[66] !== feeAssets || $[67] !== forceDeallocation || $[68] !== handleContinue || $[69] !== inputAmount || $[70] !== isPreparing || $[71] !== isRefreshing || $[72] !== isUnverifiedFinalPosition || $[73] !== recapSource || $[74] !== retainedAssets || $[75] !== reviewTrackingParams || $[76] !== simulationStatus || $[77] !== t12 || $[78] !== t8 || $[79] !== userPosition || $[80] !== vaultAddress) {
    t13 = <VaultV2OperationReviewDialog type="vaultV2Withdraw" amount={inputAmount} initialPosition={userPosition} finalPosition={displayedFinalPosition} vaultAddress={vaultAddress} isButtonDisabled={t8} isLoading={isPreparing} isRefreshing={isRefreshing} handleContinue={handleContinue} reviewTrackingParams={reviewTrackingParams} feeAssets={feeAssets} retainedAssets={retainedAssets} forceDeallocation={forceDeallocation} simulationStatus={simulationStatus} isUnverifiedFinalPosition={isUnverifiedFinalPosition} errorReason={t12} recapSource={recapSource} />;
    $[65] = displayedFinalPosition;
    $[66] = feeAssets;
    $[67] = forceDeallocation;
    $[68] = handleContinue;
    $[69] = inputAmount;
    $[70] = isPreparing;
    $[71] = isRefreshing;
    $[72] = isUnverifiedFinalPosition;
    $[73] = recapSource;
    $[74] = retainedAssets;
    $[75] = reviewTrackingParams;
    $[76] = simulationStatus;
    $[77] = t12;
    $[78] = t8;
    $[79] = userPosition;
    $[80] = vaultAddress;
    $[81] = t13;
  } else {
    t13 = $[81];
  }
  return t13;
};
export default VaultV2WithdrawReviewDialog;
function _temp() {
  return crypto.randomUUID();
}